/**
 * Locales index
 * Экспорт всех локалей
 */
import { dsLocaleMessages } from '@vinprop/design-system'
import ru from './ru'
import en from './en'
import vn from './vn'
import { routingOnboardingMessages } from './routingOnboarding'
import { agentOnboardingMessages } from './agentOnboarding'
import { agentTicketsMessages } from './agentTickets'
import { agentsAdminMessages } from './agentsAdmin'
import { agentInquiriesMessages } from './agentInquiries'
import { agentTasksMessages } from './agentTasks'
import { agentBlockingMessages } from './agentBlocking'
import { companyVerificationsMessages } from './companyVerifications'
import { adminUsersMessages } from './adminUsers'
import { adminRbacMessages } from './adminRbac'
import { adminResourcesMessages } from './adminResources'
import { rcAnalyticsMessages } from './rcAnalytics'
import { messengerShareMessages } from './messengerShare'
import { presentationsAnalyticsMessages } from './presentationsAnalytics'
import { agentEducationMessages } from './agentEducation'
import { propertyMaterialsMessages } from './propertyMaterials'
import { quizFieldsMessages } from './quizFields'
import { propertyUnitsMessages } from './propertyUnits'
import { propertyFormMessages } from './propertyForm'
import { complexCanvasMessages } from './complexCanvas'
import { priceImportMessages } from './priceImport'
import { driveImportMessages } from './driveImport'
import { propertyShowcaseMessages } from './propertyShowcase'
import { propertySalesStartMessages } from './propertySalesStart'
import { showcaseGalleryMessages } from './showcaseGallery'
import { clientLeadSourceMessages } from './clientLeadSource'
import { propertyEditTabMessages } from './propertyEditTab'
import { documentTypesMessages } from './documentTypes'
import { stageClientFieldsMessages } from './stageClientFields'
import { taskTypesMessages } from './taskTypes'
import { taskFormMessages } from './taskForm'
import { tasksBoardMessages } from './tasksBoard'
import { taskResultMessages } from './taskResult'
import { dealStorageMessages } from './dealStorage'
import { dealFileTypesMessages } from './dealFileTypes'
import { clientDuplicatesMessages } from './clientDuplicates'
import { onboardingTourMessages } from './onboardingTour'
import { accessMessages } from './access'
import { landingsMessages } from './landings'
import { campaignsMessages } from './campaigns'
import { marketingAnalyticsMessages } from './marketingAnalytics'
import { messengerAdminMessages } from './messengerAdmin'
import { messengerTemplatesMessages } from './messengerTemplates'
import { videomeetingsMessages } from './videomeetings'

type AnyRecord = Record<string, unknown>

/**
 * Рекурсивное слияние словарей переводов. Объекты сливаются вглубь, прочие
 * значения (строки и т.п.) перекрываются значением из override. Используется,
 * чтобы подмешать feature-строки (routing- и agent-онбординг) поверх базовых
 * локалей, включая переопределение примитива объектом (fields.primaryValueHint).
 */
function deepMerge<T extends AnyRecord>(base: T, override: AnyRecord): T {
  const out: AnyRecord = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const prev = out[key]
    const bothObjects =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      prev !== null &&
      typeof prev === 'object' &&
      !Array.isArray(prev)

    out[key] = bothObjects ? deepMerge(prev as AnyRecord, value as AnyRecord) : value
  }

  return out as T
}

/**
 * Feature-словари, подмешиваемые поверх базовой локали в порядке перечисления:
 * при совпадении ключа побеждает тот, что ниже. Раньше здесь было дерево
 * вложенных deepMerge, где добавление словаря означало правку скобок в трёх
 * местах, а порядок приоритета читался только по отступам.
 */
const featureMessages = [
  routingOnboardingMessages,
  agentOnboardingMessages,
  agentTicketsMessages,
  agentsAdminMessages,
  agentInquiriesMessages,
  agentTasksMessages,
  agentBlockingMessages,
  companyVerificationsMessages,
  rcAnalyticsMessages,
  messengerShareMessages,
  presentationsAnalyticsMessages,
  agentEducationMessages,
  propertyMaterialsMessages,
  quizFieldsMessages,
  propertyUnitsMessages,
  propertyFormMessages,
  complexCanvasMessages,
  priceImportMessages,
  driveImportMessages,
  propertyShowcaseMessages,
  propertySalesStartMessages,
  showcaseGalleryMessages,
  clientLeadSourceMessages,
  propertyEditTabMessages,
  documentTypesMessages,
  stageClientFieldsMessages,
  taskTypesMessages,
  taskFormMessages,
  tasksBoardMessages,
  taskResultMessages,
  dealStorageMessages,
  dealFileTypesMessages,
  clientDuplicatesMessages,
  onboardingTourMessages,
  accessMessages,
  adminUsersMessages,
  adminRbacMessages,
  adminResourcesMessages,
  landingsMessages,
  campaignsMessages,
  marketingAnalyticsMessages,
  messengerAdminMessages,
  messengerTemplatesMessages,
  videomeetingsMessages,
]

const withFeatures = <T extends AnyRecord>(base: T, locale: 'ru' | 'en' | 'vn'): T =>
  featureMessages.reduce<T>((acc, messages) => deepMerge(acc, messages[locale]), base)

/**
 * Строки компонентов дизайн-системы кладутся в основание: свои переводы CRM
 * их перекрывают, а новый ключ, появившийся в пакете, не покажется на экране
 * сырым `common.*` до правки локалей приложения.
 */
const withDesignSystem = <T extends AnyRecord>(base: T, locale: 'ru' | 'en' | 'vn'): T =>
  deepMerge(dsLocaleMessages[locale] as unknown as T, base)

export const messages = {
  ru: withDesignSystem(withFeatures(ru, 'ru'), 'ru'),
  en: withDesignSystem(withFeatures(en, 'en'), 'en'),
  vn: withDesignSystem(withFeatures(vn, 'vn'), 'vn'),
}

export type Locale = keyof typeof messages
export type MessageSchema = typeof ru

export const availableLocales: Array<{ code: Locale; name: string; flag: string }> = [
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'vn', name: 'Tiếng Việt', flag: '🇻🇳' },
]
